import CustomModalTitle from '@/new-components/CustomModal/CustomModalTitle';
import {
  Avatar,
  Box,
  Typography,
  makeStyles,
  IconButton,
} from '@material-ui/core';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
// import moment from 'moment';
import moment from 'moment-timezone';
import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { useSelector } from 'react-redux';

const HARDCODED_DOCTOR_UID = 1001;

const useStyles = makeStyles(() => ({
  chatContainer: {
    maxHeight: '90vh',
    overflowY: 'auto',
    background: '#fff',
    borderRadius: 12,
    padding: '16px 12px',
    margin: '0 8px',
    minHeight: 400,
    position: 'relative',
  },

  avatarDoctor: {
    background: '#E2F2FF',
    color: '#3776AB',
    fontSize: 14,
  },
  avatarPatient: {
    background: '#FFE9FB',
    color: '#AD5E99',
    fontSize: 14,
  },
  name: {
    fontWeight: 600,
    fontSize: 14,
    marginBottom: 2,
    color: '#333',
  },
  time: {
    fontSize: 12,
    color: '#999',
    marginLeft: 8,
    marginRight: 8,
    marginTop: 2,
  },
  scrollButton: {
    position: 'absolute',
    bottom: 20,
    right: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 1)',
    },
  },
}));

const formatSecondsToMMSS = (seconds, currentTime) => {
  const time = moment(currentTime).add(seconds, 'seconds').format('h:mm A');
  return `${time}`;
};

const mergeAndSortMessages = (messagesDoctor = [], messagesPatient = []) => {
  const localtz = moment.tz.guess(); // user's timezone

  // Add a role to each message for rendering
  const doctorMsgs = messagesDoctor.map((msg) => ({
    ...msg,
    time: moment.utc(msg.end_time).tz(localtz).format('h:mm A'),
    role: 'doctor',
  }));

  const patientMsgs = messagesPatient.map((msg) => ({
    ...msg,
    time: moment.utc(msg.end_time).tz(localtz).format('h:mm A'),
    role: 'patient',
  }));
  // Assume time is a string like '11:01 AM', sort by time string (for demo)
  // In real app, use a timestamp for accurate sorting
  const all = [...doctorMsgs, ...patientMsgs];
  all.sort((a, b) =>
    moment(a.end_time).unix() > moment(b.end_time).unix() ? 1 : -1
  );
  return all;
};

const StreamTranscription = ({
  onClose,
  messagesPatient,
  messagesDoctor,
  data,
  oldData,
  startCallTime,
}) => {
  const localtz = moment.tz.guess(); // user's timezone
  const classes = useStyles();
  const userInfo = useSelector((state) => state.auth.userInfo);
  const messages = useSelector((state) => state.waitingRoom.messages);

  const messageFormatted = useMemo(() => {
    return Object.values(messages).sort((a, b) =>
      moment(a.end_time).unix() > moment(b.end_time).unix() ? 1 : -1
    );
  }, [messages]);
  // const messages = mergeAndSortMessages(messagesDoctor, messagesPatient);
  const resolveHistoryRole = (item) => {
    const speakerNum =
      typeof item?.speaker === 'string' ? Number(item.speaker) : item?.speaker;

    if (speakerNum === HARDCODED_DOCTOR_UID) return 'doctor';
    if (speakerNum != null) return 'patient';
    if (item.tag === 'doctor') return 'doctor';
    return 'patient';
  };

  const oldMessages =
    oldData?.map((item) => ({
      ...item,
      time: moment.utc(item.end_time).tz(localtz).format('h:mm A'),
      role: resolveHistoryRole(item),
    })) || [];
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const [currentTime] = useState(moment(startCallTime));
  const [showScrollButton, setShowScrollButton] = useState(false);

  const isNearBottom = useCallback(() => {
    if (!chatContainerRef.current) return true;
    const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
    return scrollHeight - scrollTop - clientHeight < 100;
  }, []);

  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
    }
  }, []);

  const handleScroll = useCallback(() => {
    setShowScrollButton(!isNearBottom());
  }, [isNearBottom]);

  useEffect(() => {
    const container = chatContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  useEffect(() => {
    if (isNearBottom()) {
      scrollToBottom();
    }
  }, [messages, isNearBottom, scrollToBottom]);

  return (
    <Box>
      <CustomModalTitle
        onClose={onClose}
        style={{ padding: '20px 20px 16px' }}
        styleButton={{ top: 20 }}
      >
        Transcription
      </CustomModalTitle>
      <Box className={classes.chatContainer} ref={chatContainerRef}>
        {[...oldMessages, ...messageFormatted].map((msg, idx) => {
          const isDoctor = msg.role === 'doctor';
          return (
            <Box key={idx} display="flex" mb={2} gridGap={8}>
              <Avatar
                className={
                  isDoctor ? classes.avatarDoctor : classes.avatarPatient
                }
              >
                {isDoctor
                  ? userInfo.fullName?.match(/\b\w/g).join('')
                  : (data?.FullName || data.PatientFullName)
                      ?.match(/\b\w/g)
                      .join('')}
              </Avatar>
              <Box>
                <Box display="flex" alignItems="center">
                  <Typography className={classes.name}>
                    {isDoctor
                      ? 'Dr. ' + (userInfo?.FullName || userInfo.fullName)
                      : data?.FullName || data.PatientFullName}
                  </Typography>
                  <Typography className={classes.time}>
                    {moment.utc(msg.start_time).tz(localtz).format('h:mm A')}
                  </Typography>
                </Box>
                <Box>{msg.transcript}</Box>
              </Box>
            </Box>
          );
        })}
        <div ref={messagesEndRef} />
      </Box>
      {showScrollButton && (
        <IconButton
          className={classes.scrollButton}
          onClick={scrollToBottom}
          size="small"
        >
          <KeyboardArrowDownIcon />
        </IconButton>
      )}
    </Box>
  );
};

export default StreamTranscription;
